/* eslint-disable @typescript-eslint/no-unused-vars */
import * as Sentry from '@sentry/react';
import toast from 'react-hot-toast';
import Swal from 'sweetalert2';
import { create } from 'zustand';
import api from '../common/api/api';
import graphqlClient from '../common/api/graphql';
import {
  CreateSessionEvent,
  EnableDisableIA,
  GetBotSession,
  GetSessionBot,
  GetSessionLastMessage,
  GetSessionMessagesNew,
  SetSessionDisableFollowup,
  UpdateContactTags,
  UpdateLastMessageViewed,
} from '../common/queries/queries';
import { auth } from '../firebaseConfig';
import {
  lastMessageReceiptFields,
  resolveErrorCodeFromText,
} from '../src/components/apps/chats/messageStatus';
import {
  getContactTags,
  hasSuspiciousTag,
  SUSPICIOUS_CONTACT_TAG,
  withoutSuspiciousTag,
} from '../src/constants/suspiciousActivity';
import { extractWhatsappNumber, handleUploadFiles } from '../src/shared/utilities';
import {
  mediaResponseErrorText,
  plainMediaErrorCode,
  resolveMediaSendFailure,
  WaMediaMessageResponse,
} from '../src/shared/whatsappSticker';
import { ApiResponseType } from '../src/types/apiTypes';
import type { Resource } from '../src/types/apps/resource';
import {
  TemplatesResponseType, TemplateWithComponents, WATemplateSendType, WhatsappTemplateType,
} from '../src/types/apps/waIntegrationsTypes';
import { applyDraftToChats, applyDraftToMessages } from '../src/components/apps/chats/draftMessages';
import { EmailReply, emailReplyPayload, emailReplySent } from '../common/api/draftApi';
import useUserStore from './userStore';
import { loadOrgMembers } from '../src/utils/orgMembersApi';
import {
  clearSessionSuspicious,
  type CreatedEvent,
  isChatActionsRest,
  markSessionRead,
  setSessionAi,
  setSessionFollowupDisabled,
} from '../src/components/apps/chats/chatActionsApi';

function isResource(media: File | Resource): media is Resource {
  return !(media instanceof File) && typeof (media as Resource).file_url === 'string';
}

function extractSendErrorMessage(data: unknown, fallback = 'Error al enviar mensaje'): string {
  if (typeof data === 'string' && data.trim()) return data;
  if (data && typeof data === 'object') {
    const body = data as { error?: unknown; message?: unknown; detail?: unknown };
    if (typeof body.error === 'string' && body.error.trim()) return body.error;
    if (typeof body.message === 'string' && body.message.trim()) return body.message;
    if (typeof body.detail === 'string' && body.detail.trim()) return body.detail;
  }
  return fallback;
}

function buildFailedMessageFields(errorMessage: string) {
  return {
    localStatus: 'error' as const,
    failed_at: new Date().toISOString(),
    error_message: errorMessage,
    error_code: resolveErrorCodeFromText(errorMessage),
  };
}

async function promptInsufficientCredits(): Promise<void> {
  try {
    const result = await Swal.fire({
      icon: 'warning',
      title: 'Créditos insuficientes',
      text: 'No tienes créditos suficientes para enviar este mensaje. Recarga créditos para continuar.',
      showCancelButton: true,
      confirmButtonText: 'Recargar créditos',
      cancelButtonText: 'Cerrar',
      reverseButtons: true,
    });
    if (result.isConfirmed && typeof window !== 'undefined') {
      window.location.assign('/organization/billing');
    }
  } catch {
    toast.error('No tienes créditos suficientes para enviar este mensaje.');
  }
}

export function getOutboundMessagingEndpoint(source: string | undefined): string {
  if (source === 'messenger' || source === 'telegram' || source === 'gmail') return 'messaging/send-message';
  return 'conversations/send-message';
}

interface ContactInfo {
  id: string;
  name?: string;
  last_name?: string;
}

interface VariablesType {
  bot_id_param: string;
  limit: number;
  offset: number;
  where?: object;
  order_by?: any[];
}
interface ChatStoreState {
  currentChat: any;
  newChat: any;
  whatsappTemplates: WhatsappTemplateType[];
  loadedTemplate?: {
    template: WhatsappTemplateType,
    details: TemplateWithComponents,
  };
  chats: any[];
  loading: boolean;
  isPristineEmpty: boolean;
  isSharedUnread: boolean;
  currentPlayingAudio: string | null;
  setCurrentPlayingAudio: (url: string | null) => void;
  // Conversational feedback diagnostic panel: message currently open in the right column.
  diagnosticMessageId: string | null;
  openDiagnostic: (messageId: string) => void;
  closeDiagnostic: () => void;
  setNewChat: (newChat: any) => void;
  setCurrentChat: (chatId: any, userId: string, orgId: string, isSharedUnread: boolean, botId: string, salesAssignedId?: string) => Promise<void>;
  updateChatCounter: (userId: string, lastMsgReadId: string, orgId: string, isSharedUnread: boolean) => void;
  clearChats: (resetHasMore?: boolean) => void;
  fetchChats: (
    botId: string,
    limit: number,
    offset: number,
    leadStatus: string,
    whereClause?: object,
    sortOrder?: 'newest' | 'oldest' | 'fromDate'
  ) => Promise<void>;
  updateChats: (updatedBlock: any, activeTab: string) => void;
  updateChatsFromBotSessions: (updatedBlock: any, activeTab: string, effectiveUserId?: string, filters?: any) => void;
  sortChats: (chats: any[], sortOrder?: 'newest' | 'oldest' | 'fromDate') => any[];
  postNewMessage: (id: string, message: string, track_id: string) => Promise<void>;
  addOptimisticMessage: (
    track_id: string,
    message: string,
    localStatus?: 'sending' | 'buffering',
  ) => void;
  updateOptimisticMessage: (
    track_id: string,
    updates: { text?: string; localStatus?: 'sending' | 'buffering' | 'error' | null },
  ) => void;
  dispatchMessage: (bot_id: string, message: string, track_id: string) => Promise<any>;
  dispatchInstagramMessage: (
    session_id: string,
    message: string,
    track_id: string,
    channel?: string,
  ) => Promise<any>;
  deleteChat: (id: string) => void;
  enableDisableIA: () => void;
  clearSuspiciousActivity: () => Promise<boolean>;
  setSessionDisableFollowup: (disableFollowup: boolean) => Promise<void>;
  loadBotWhatappTemplates: (botId: string) => void;
  changeSelectedTemplate: (botID: string, template: WhatsappTemplateType) => Promise<void>;
  onWATemplateSend: (botID: string, payload: WATemplateSendType) => Promise<{
    success: boolean;
    error?: string;
  }>;
  deselectTemplate: () => void;
  loadMoreChats: (
    botId: string,
    limit: number,
    offset: number,
    leadStatus: string,
    whereClause?: object,
    sortOrder?: 'newest' | 'oldest' | 'fromDate'
  ) => Promise<number>;
  loadingMore: boolean;
  offset: number;
  hasMore: boolean;
  setOffset: (newOffset: number) => void;
  setHasMore: (value: boolean) => void;
  setLoadingMore: (value: boolean) => void;
  postingNewMessage: boolean;
  setPostingNewMessage: (value: boolean) => void;
  validateSessionBot: (sessionId: string) => Promise<string | null>;
  keywordSearchLoading: boolean;
  keywordSearchResults: any[] | null;
  performKeywordSearch: (botId: string, userId: string, searchTerm: string, offset?: number) => Promise<void>;
  clearKeywordSearch: () => void;
  loadingTemplate: boolean;
  contactsMap: Record<string, ContactInfo>;
  updateContact: (sessionId: string, contactInfo: ContactInfo) => void;
  updateChatsState: (updatedChats: any[]) => void;
  keywordSearchOffset: number;
  keywordSearchHasMore: boolean;
  fetchChat: (chatId: string, userId: string, botId: string) => Promise<any>;
  // Nuevas funciones para enviar media y audio
  sendMediaMessage: (
    bot_id: string, media: File | Resource, mediaType: string, track_id: string
  ) => Promise<void>;
  sendAudioMessage: (bot_id: string, audio_url: string, track_id: string, session_id: string) => Promise<void>;
  postInstagramMessage: (session_id: string, message: string, track_id: string, channel?: string) => Promise<void>;
  sendInstagramMedia: (
    session_id: string, media: File | Resource, mediaType: string, track_id: string, channel?: string
  ) => Promise<void>;
  /** A Gmail reply written by a person: text, html and at most one file in one email. */
  sendGmailReply: (
    session_id: string, reply: EmailReply, track_id: string,
  ) => Promise<{ ok: boolean; message?: string }>;
  messages: any[];
  setMessages: (messages: any[]) => void;
  applyDraftResolution: (
    messageId: string,
    status: 'sent' | 'discarded',
    text?: string,
    media?: Record<string, unknown>,
  ) => void;
  leadStatusFilter: string | null;
  addOptimisticNote: (noteData: any, contactData?: any) => void;
  addOptimisticEvent: (eventData: any, eventId: string, createdAt: string, contactData?: any) => void;
  addOptimisticEventToSidebar: (eventData: any, eventId: string, createdAt: string) => void;
  sidebarEventCallbacks: {
    addEvent?: (event: any) => void;
  };
  setSidebarEventCallbacks: (callbacks: { addEvent?: (event: any) => void }) => void;
  retryMessage: (track_id: string, bot_id: string) => Promise<void>;
}

// Sessions carry their bot; the current bot is the fallback the chat already used elsewhere.
const chatBotId = (chat: any): string => chat?.bot_id || useUserStore.getState().currentBot?.id;

// Helper function to get agent name by ID
const getAgentNameById = (agentId: string | null): string | null => {
  if (!agentId) return null;

  const userStore = useUserStore.getState();
  const agent = userStore.organizationUsers.find((user) => user.id === agentId);
  return agent?.name || null;
};

type StoreSet = (fn: (state: ChatStoreState) => Partial<ChatStoreState>) => void;
type StoreGet = () => ChatStoreState;

async function postWaTrackedMessage(
  set: StoreSet,
  get: StoreGet,
  {
    bot_id,
    track_id,
    content,
    action,
  }: {
    bot_id: string;
    track_id: string;
    content: string;
    action: 'dispatchMessage' | 'retryMessage';
  },
) {
  const { user } = useUserStore.getState();
  const { currentChat } = get();
  const phoneNumber = extractWhatsappNumber(currentChat?.metadata?.local_session_id);
  const failureLabel = action === 'retryMessage'
    ? 'WA Message Retry Failed'
    : 'WA Message Send Failed';

  const sentryContext = {
    bot_id,
    track_id,
    session_id: currentChat?.id,
    phone_number: phoneNumber,
    user_id: user?.uid,
    ...(action === 'retryMessage' ? { is_retry: true } : {}),
  };

  const markFailed = (errorMessage: string) => {
    set((state) => ({
      messages: state.messages.map((msg) => (msg.track_id === track_id
        ? {
          ...msg,
          ...buildFailedMessageFields(errorMessage),
        }
        : msg)),
    }));
  };

  try {
    const response = await api.post(`wa/${bot_id}/messages`, {
      to: phoneNumber,
      content,
      session_id: currentChat.id,
      track_id,
    });

    if (!response.success) {
      const isTimeout = response.data?.includes?.('timeout')
        || response.data?.code === 'ECONNABORTED';
      const errorType = isTimeout ? 'timeout' : 'api_error';
      const errorMessage = isTimeout
        ? 'Request timeout - el servidor no respondió a tiempo'
        : extractSendErrorMessage(response.data);

      Sentry.captureException(new Error(`${failureLabel}: ${errorType}`), {
        tags: {
          component: 'chatStore',
          action,
          error_type: errorType,
        },
        extra: {
          ...sentryContext,
          response_data: response.data,
        },
      });

      markFailed(errorMessage);
      throw new Error(errorMessage);
    }

    if (response.data?.status === 'sent') {
      set((state) => ({
        messages: state.messages.map((msg) => (msg.track_id === track_id
          ? {
            ...msg,
            localStatus: null,
            sent_at: new Date().toISOString(),
            failed_at: null,
            error_message: null,
            error_code: null,
          }
          : msg)),
      }));
    } else {
      const unexpectedStatusMessage = extractSendErrorMessage(
        response.data,
        'El mensaje no pudo ser enviado',
      );

      Sentry.captureException(new Error(`${failureLabel}: unexpected_status`), {
        tags: {
          component: 'chatStore',
          action,
          error_type: 'unexpected_status',
        },
        extra: {
          ...sentryContext,
          response_status: response.data?.status,
          response_data: response.data,
        },
      });

      markFailed(unexpectedStatusMessage);
      throw new Error(unexpectedStatusMessage);
    }

    return response.data;
  } catch (error: any) {
    const catchIsTimeout = error.code === 'ECONNABORTED'
      || error.message?.includes('timeout');
    const catchIsNetworkError = error.message === 'Network Error' || !navigator.onLine;

    let catchErrorType = 'unknown';
    let userFriendlyMessage = error.message || 'Error desconocido';

    if (catchIsTimeout) {
      catchErrorType = 'timeout';
      userFriendlyMessage = 'Tiempo de espera agotado. Verifica tu conexión e intenta de nuevo.';
    } else if (catchIsNetworkError) {
      catchErrorType = 'network_error';
      userFriendlyMessage = 'Error de conexión. Verifica tu internet e intenta de nuevo.';
    }

    if (!error.message?.includes(failureLabel)) {
      Sentry.captureException(error, {
        tags: {
          component: 'chatStore',
          action,
          error_type: catchErrorType,
        },
        extra: {
          ...sentryContext,
          error_code: error.code,
          error_message: error.message,
          is_online: navigator.onLine,
        },
      });
    }

    markFailed(userFriendlyMessage);
    throw error;
  }
}

// Rapid switch clicks collapse into one request carrying the final state.
const AI_TOGGLE_DEBOUNCE_MS = 400;
const aiTogglePending = new Map<string, { timer: ReturnType<typeof setTimeout>; persistedState: boolean }>();
// Persists run one after another so the server never sees them out of order.
let aiToggleQueue: Promise<void> = Promise.resolve();

const persistAiToggle = async (
  get: () => ChatStoreState,
  currChat: any,
  previousState: boolean,
  newState: boolean,
  applyAiState: (value: boolean) => void,
) => {
  try {
    const { user } = useUserStore.getState();

    const eventType = newState ? 'ai_enabled' : 'ai_disabled';
    const eventDescription = newState
      ? `IA habilitada por ${user.name || user.email}`
      : `IA deshabilitada por ${user.name || user.email}`;
    let createdEvent: CreatedEvent | null = null;
    if (isChatActionsRest()) {
      // One call: flag, Redis cache sync and the activity event, in that order.
      try {
        createdEvent = await setSessionAi(chatBotId(currChat), currChat.id, newState);
      } catch (restError) {
        applyAiState(previousState);
        throw restError;
      }
    } else {
      // 1. Actualizar el estado de IA
      try {
        await graphqlClient.mutate({
          mutation: EnableDisableIA,
          variables: {
            id: currChat.id,
            ai_enabled: newState,
          },
          fetchPolicy: 'network-only',
        });
      } catch (mutationError) {
        applyAiState(previousState);
        throw mutationError;
      }

      // 1.5. Sync Redis cache with new ai_enabled value
      try {
        await api.post('messaging/sessions/sync-cache', {
          session_id: currChat.id,
        });
      } catch (syncError) {
        console.error('Error syncing cache, but continuing...', syncError);
        // Don't throw error to avoid interrupting the flow
      }

      // 2. Create logging event
      const eventResponse = await graphqlClient.mutate({
        mutation: CreateSessionEvent,
        variables: {
          session_id: currChat.id,
          event_type: eventType,
          event_category: 'system',
          description: eventDescription,
          metadata: {
            toggled_by: user.email,
            previous_state: previousState,
            new_state: newState,
            reason: 'Manual toggle from UI',
          },
          created_by: user.uid,
          created_by_type: 'user',
          created_by_name: user.name || user.email,
          severity: 'info',
        },
        fetchPolicy: 'network-only',
      });
      createdEvent = eventResponse.data?.insert_bot_session_events_one ?? null;
    }

    // Agregar evento optimista al chat
    if (createdEvent) {
      const { addOptimisticEvent } = get();
      addOptimisticEvent(
        {
          event_type: eventType,
          event_category: 'system',
          description: eventDescription,
          created_by_name: user.name || user.email,
          severity: 'info',
          metadata: {
            toggled_by: user.email,
            previous_state: previousState,
            new_state: newState,
            reason: 'Manual toggle from UI',
          },
        },
        `event-${createdEvent.id}`,
        createdEvent.created_at,
        currChat.contact || {
          id: currChat.contact_id || '',
          name: currChat.contact_name || '',
          last_name: currChat.contact_last_name || '',
          email: currChat.contact_email || '',
          phone: currChat.contact_phone || '',
          __typename: 'contacts',
        },
      );

      // Also add to sidebar
      get().addOptimisticEventToSidebar(
        {
          event_type: eventType,
          event_category: 'system',
          description: eventDescription,
          created_by_name: user.name || user.email,
          severity: 'info',
          metadata: {
            toggled_by: user.email,
            previous_state: previousState,
            new_state: newState,
            reason: 'Manual toggle from UI',
          },
        },
        `event-${createdEvent.id}`,
        createdEvent.created_at,
      );
    }

  } catch (error) {
    console.error('Error updating chat AI status', error);
  }
};

const SEARCH_PAGE_SIZE = 20;

const useChatStore = create<ChatStoreState>((set, get) => ({
  currentChat: null,
  newChat: null,
  loading: true,
  isPristineEmpty: false,
  chats: [],
  whatsappTemplates: [],
  loadedTemplate: null,
  loadingMore: false,
  postingNewMessage: false,
  offset: 0, // Fixed: Should start at 0, not 20
  hasMore: true,
  isSharedUnread: false,
  currentPlayingAudio: null,
  setCurrentPlayingAudio: (url: string | null) => set({ currentPlayingAudio: url }),
  diagnosticMessageId: null,
  openDiagnostic: (messageId: string) => set({ diagnosticMessageId: messageId }),
  closeDiagnostic: () => set({ diagnosticMessageId: null }),
  keywordSearchLoading: false,
  keywordSearchResults: null,
  loadingTemplate: false,
  contactsMap: {},
  keywordSearchOffset: 0,
  keywordSearchHasMore: true,
  setOffset: (newOffset: number) => set({ offset: newOffset }),
  setHasMore: (value: boolean) => set({ hasMore: value }),
  setLoadingMore: (value: boolean) => set({ loadingMore: value }),
  setNewChat: (newChat) => set({ newChat }),
  setPostingNewMessage: (value: boolean) => set({ postingNewMessage: value }),
  messages: [],
  setMessages: (messages: any[]) => set({ messages }),
  // A draft resolved (approved, discarded, or found gone): mirror it on the bubble without a refetch.
  applyDraftResolution: (messageId, status, text, media) => set((state) => ({
    messages: applyDraftToMessages(state.messages, messageId, status, text, media),
    chats: applyDraftToChats(state.chats, state.currentChat?.id, status),
  })),
  leadStatusFilter: null,
  sidebarEventCallbacks: {},

  enableDisableIA: () => {
    const currChat = get().currentChat;
    if (!currChat?.id) return;
    const chatId = currChat.id;
    const newState = !currChat.ai_enabled;
    const pending = aiTogglePending.get(chatId);
    // State the server holds: the one before the first click of this burst.
    const persistedState = pending ? pending.persistedState : !!currChat.ai_enabled;
    if (pending) clearTimeout(pending.timer);

    // Optimistic: flip the switch now; the chat may change while requests are in flight.
    const applyAiState = (value: boolean) => set((state) => ({
      currentChat: state.currentChat?.id === chatId
        ? { ...state.currentChat, ai_enabled: value }
        : state.currentChat,
      chats: state.chats.map((chat) => (
        chat.id === chatId ? { ...chat, ai_enabled: value } : chat
      )),
    }));
    applyAiState(newState);

    const timer = setTimeout(() => {
      aiTogglePending.delete(chatId);
      // Toggled back to where it started: nothing to send.
      if (newState === persistedState) return;
      aiToggleQueue = aiToggleQueue.then(
        () => persistAiToggle(get, currChat, persistedState, newState, applyAiState),
      );
    }, AI_TOGGLE_DEBOUNCE_MS);
    aiTogglePending.set(chatId, { timer, persistedState });
  },

  clearSuspiciousActivity: async () => {
    const currChat = get().currentChat;
    if (!currChat?.id) return false;

    const contactId = currChat.contact_id || currChat.contact?.id;
    if (!contactId) return false;

    const currentTags = getContactTags(currChat);
    if (!hasSuspiciousTag(currChat)) return false;

    let newTags = withoutSuspiciousTag(currentTags);

    try {
      if (isChatActionsRest()) {
        newTags = await clearSessionSuspicious(chatBotId(currChat), currChat.id);
      } else {
        await graphqlClient.mutate({
          mutation: UpdateContactTags,
          variables: {
            id: contactId,
            tags: newTags,
          },
          fetchPolicy: 'network-only',
        });

        try {
          await api.post('messaging/sessions/sync-cache', {
            session_id: currChat.id,
          });
        } catch (syncError) {
          console.error('Error syncing cache after clearing suspicious tag', syncError);
        }
      }

      const updatedChat = {
        ...currChat,
        contact_tags: newTags,
        contact: currChat.contact
          ? { ...currChat.contact, tags: newTags }
          : currChat.contact,
      };

      set({
        currentChat: updatedChat,
        chats: get().chats.map((chat) => (chat.id === currChat.id
          ? { ...chat, contact_tags: newTags }
          : chat)),
      });

      return true;
    } catch (error) {
      console.error('Error clearing suspicious activity tag', error);
      return false;
    }
  },

  setSessionDisableFollowup: async (disableFollowup: boolean) => {
    const currChat = get().currentChat;
    if (!currChat?.id) return;
    try {
      if (isChatActionsRest()) {
        await setSessionFollowupDisabled(chatBotId(currChat), currChat.id, disableFollowup);
      } else {
        await graphqlClient.mutate({
          mutation: SetSessionDisableFollowup,
          variables: {
            id: currChat.id,
            disable_followup: disableFollowup,
          },
          fetchPolicy: 'network-only',
        });
      }
      set({
        currentChat: { ...currChat, disable_followup: disableFollowup },
      });
      const updatedChats = get().chats.map((chat) => (chat.id === currChat.id ? { ...chat, disable_followup: disableFollowup } : chat));
      set({ chats: updatedChats });
    } catch (error) {
      console.error('Error updating session follow-up flag', error);
      toast.error('Could not update follow-up setting for this chat');
    }
  },

  validateSessionBot: async (sessionId: string) => {
    try {
      const res = await graphqlClient.query({
        query: GetSessionBot,
        variables: { session_id: sessionId },
      });

      if (!res.data?.bot_sessions_by_pk) {
        console.error('Session not found or invalid response');
        return null;
      }

      return res.data.bot_sessions_by_pk.bot_id;
    } catch (error) {
      console.error('Error validating session bot:', error);
      return null;
    }
  },

  sortChats: (chats, sortOrder = 'newest') => [...chats].sort((a, b) => {
    const getLastMessageDate = (chat) => {
      if (chat.last_message_date) {
        return new Date(chat.last_message_date).getTime();
      }
      return new Date(0).getTime();
    };
    const dateA = getLastMessageDate(a);
    const dateB = getLastMessageDate(b);
    return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
  }),

  clearChats: async (resetHasMore = true) => {
    set({
      chats: [],
      offset: 0,
      isPristineEmpty: false,
      hasMore: resetHasMore ? true : get().hasMore,
    });
  },

  deleteChat: async (id: string) => {
    const filteredChats = get().chats.filter((chat) => chat.id !== id);
    set({ chats: filteredChats });
    const updatedChats = get().chats;
    if (updatedChats.length === 0) {
      set({ currentChat: null });
    } else {
      set({ currentChat: updatedChats[0] });
    }
  },

  onWATemplateSend: async (botID: string, payload: WATemplateSendType) => {
    try {
      const res: ApiResponseType<{ data: { message_id: string; session_id: string }, error: boolean }> = await api.post(`wa/${botID}/templates/send`, payload);

      if (!res.success) {
        const errorData = res.data as any;
        const errorMessage = errorData?.detail || errorData?.message || 'Error al enviar el template';
        throw new Error(errorMessage);
      }

      if (res.data.error) {
        const errorMessage = (res.data.data as any)?.message?.message || (res.data.data as any)?.message || 'Error al enviar el template';
        throw new Error(errorMessage);
      }

      set({
        loadedTemplate: null,
        newChat: null,
      });

      return { success: true };
    } catch (error) {
      console.error('onWATemplateSend: error', error);
      return {
        success: false,
        error: error.message || 'Ha ocurrido un error',
      };
    }
  },

  updateChatCounter: async (userId: string, lastMsgReadId: string, orgId: string, isSharedUnread: boolean) => {
    if (!lastMsgReadId || typeof lastMsgReadId !== 'string' || lastMsgReadId.startsWith('event-')) {
      return;
    }
    const currentChatId = get().currentChat.id;
    const isSharedUnreadFromBot = useUserStore.getState().currentBot?.settings?.config?.shared_unread === true;
    const effectiveSharedUnread = isSharedUnreadFromBot;
    try {
      // Evitar mutación si no hay cambios necesarios
      const { currentChat } = get();
      const myUMV = currentChat?.users_messages_viewed?.find((u) => u.user_id === userId);
      if (
        myUMV
        && myUMV.last_msg_read_id === lastMsgReadId
        && (myUMV.unread_count || 0) === 0
        && myUMV.manual_unread === false
      ) {
        return;
      }

      if (isChatActionsRest()) {
        // The server reads the bot's shared_unread setting and fans out to the members, as below.
        await markSessionRead(chatBotId(get().currentChat), currentChatId, lastMsgReadId);
      } else {
        let objects;

        if (effectiveSharedUnread) {
          const users = await loadOrgMembers(orgId) as { user_id: string }[];
          const uniqueByUserId = Array.from(new Map(users.map((u) => [u.user_id, u])).values());
          objects = uniqueByUserId.map((u) => ({
            user_id: u.user_id,
            last_msg_read_id: lastMsgReadId,
            unread_count: 0,
            session_id: currentChatId,
            updated_at: new Date().toISOString(),
            manual_unread: false,
          }));
        } else {
          objects = [{
            user_id: userId,
            last_msg_read_id: lastMsgReadId,
            unread_count: 0,
            session_id: currentChatId,
            updated_at: new Date().toISOString(),
            manual_unread: false,
          }];
        }

        await graphqlClient.mutate({
          mutation: UpdateLastMessageViewed,
          variables: { objects },
          fetchPolicy: 'no-cache',
        });
      }

      set((state) => {
        const updatedChats = state.chats.map((chat) => {
          if (chat.id !== currentChatId) return chat;
          const updatedUsersMessagesViewed = Array.isArray(chat.users_messages_viewed)
            ? chat.users_messages_viewed.map((u) => (u.user_id === userId
              ? {
                ...u,
                unread_count: 0,
                last_msg_read_id: lastMsgReadId,
                manual_unread: false,
                updated_at: new Date().toISOString(),
              }
              : u))
            : [
              {
                user_id: userId,
                last_msg_read_id: lastMsgReadId,
                unread_count: 0,
                session_id: currentChatId,
                updated_at: new Date().toISOString(),
                manual_unread: false,
              },
            ];

          return {
            ...chat,
            unread_count: 0,
            manual_unread: false,
            users_messages_viewed: updatedUsersMessagesViewed,
          };
        });

        const curr = state.currentChat;
        let updatedCurrentChat = curr;
        if (curr?.id === currentChatId) {
          const updatedUsersMessagesViewed = Array.isArray(curr.users_messages_viewed)
            ? curr.users_messages_viewed.map((u) => (u.user_id === userId
              ? {
                ...u,
                unread_count: 0,
                last_msg_read_id: lastMsgReadId,
                manual_unread: false,
                updated_at: new Date().toISOString(),
              }
              : u))
            : [
              {
                user_id: userId,
                last_msg_read_id: lastMsgReadId,
                unread_count: 0,
                session_id: currentChatId,
                updated_at: new Date().toISOString(),
                manual_unread: false,
              },
            ];
          updatedCurrentChat = {
            ...curr,
            manual_unread: false,
            users_messages_viewed: updatedUsersMessagesViewed,
          };
        }

        return {
          chats: updatedChats,
          currentChat: updatedCurrentChat,
        };
      });
    } catch (error) {
      console.error('Error updating chat unread counter', error);
    }
  },

  fetchChat: async (chatId: string, userId: string, botId: string) => {
    try {
      // Validar que chatId sea válido antes de hacer la query
      if (!chatId || chatId === 'null' || chatId === 'undefined') {
        return null;
      }

      const res = await graphqlClient.query({
        query: GetBotSession,
        variables: {
          session_id: chatId,
          user_id: userId,
          bot_id: botId,
        },
        fetchPolicy: 'no-cache',
      });
      return res.data.bot_sessions[0];
    } catch (error) {
      console.error('Error fetching chat:', error);
      return null;
    }
  },

  setCurrentChat: async (chatId: any, userId: string, orgId: string, isSharedUnread: boolean, botId: string, salesAssignedId?: string) => {
    // Validar que chatId sea válido antes de continuar
    if (!chatId || chatId === 'null' || chatId === 'undefined') {
      return;
    }

    // Switching chats closes any open Sherlock diagnostic so the right panel
    // resets to the contact view instead of showing the previous chat's report.
    if (get().diagnosticMessageId) {
      set({ diagnosticMessageId: null });
    }

    const searchResults = get().keywordSearchResults;
    let chat = searchResults?.find((c) => c.id === chatId);
    const isFromKeywordSearch = !!chat;

    if (!chat) {
      const chatInStore = get().chats.find((c) => c.id === chatId) || null;
      chat = chatInStore;
      if (!chat) {
        const chatInDb = await get().fetchChat(chatId, userId, botId) || null;
        chat = chatInDb;
      }
    }

    // If chat is from keyword search, ensure we have complete data including assignment info
    // Fetch full chat data to ensure all fields are present, especially for assignment data
    if (isFromKeywordSearch && chat) {
      const fullChatData = await get().fetchChat(chatId, userId, botId);
      if (fullChatData) {
        chat = fullChatData;
      }
    }

    if (chat) {
      // Ensure sales_assigned_name is set if sales_assigned_id exists
      if (chat.sales_assigned_id && !chat.sales_assigned_name) {
        chat.sales_assigned_name = getAgentNameById(chat.sales_assigned_id);
      }

      // Actualizar sales_assigned_id si se proporciona (incluyendo null para desasignar)
      const updatedChat = salesAssignedId !== undefined ? {
        ...chat,
        sales_assigned_id: salesAssignedId,
        sales_assigned_name: getAgentNameById(salesAssignedId),
      } : chat;
      set({ currentChat: updatedChat });

      // También actualizar en la lista de chats para mantener consistencia
      if (salesAssignedId !== undefined) {
        set((state) => ({
          chats: state.chats.map((c) => (c.id === chatId
            ? {
              ...c,
              sales_assigned_id: salesAssignedId,
              sales_assigned_name: getAgentNameById(salesAssignedId),
            }
            : c)),
        }));
      }

      const umv = updatedChat.users_messages_viewed?.find((u) => u.user_id === userId);
      const lastId = updatedChat.last_message_id;

      const needsMarkRead = !!lastId && (
        (umv?.unread_count || 0) > 0 || umv?.manual_unread === true
      ) && umv?.last_msg_read_id !== lastId;

      if (needsMarkRead) {
        get().updateChatCounter(userId, lastId, orgId, isSharedUnread);
      }
    } else {
      console.error('Error al cargar el chat');
    }
  },

  updateChatsFromBotSessions: async (updatedBlock: any, activeTab: string, effectiveUserId?: string, filters?: any) => {
    try {
      if (!updatedBlock?.data?.bot_sessions_stream) {
        return;
      }

      const currentUserId = effectiveUserId || useUserStore.getState().user.uid;

      await Promise.all(updatedBlock.data.bot_sessions_stream?.map(async (session) => {
        const isCurrentOpen = get().currentChat?.id === session.id;

        // Obtener unread count del usuario actual
        const myUMV = session.users_messages_viewed?.find((u) => u.user_id === currentUserId);
        const effectiveUnread = isCurrentOpen ? 0 : (myUMV?.unread_count || 0);

        // Si es el chat actual y hay unread, preparar para auto-marcar como leído
        let adjustedSession = session;
        const isValidMessageId = (id: any) => typeof id === 'string' && id.length > 0 && !id.startsWith('event-');
        if (isCurrentOpen && myUMV?.unread_count > 0 && isValidMessageId(session.last_message_id)) {
          // Ajustar la data del stream para que se muestre sin unread
          adjustedSession = {
            ...session,
            users_messages_viewed: session.users_messages_viewed?.map((umv) => (umv.user_id === currentUserId
              ? { ...umv, unread_count: 0, manual_unread: false }
              : umv)) || [],
          };

          // Hacer la mutación async para sincronizar con backend (sin await para no bloquear)
          setTimeout(() => {
            const userStore = useUserStore.getState();
            get().updateChatCounter(
              currentUserId,
              session.last_message_id,
              userStore.currentOrganization?.id,
              get().isSharedUnread,
            );
          }, 100);
        }

        const contactTags = adjustedSession.contact?.tags || [];
        const shouldIncludeChat = () => {
          if (activeTab === 'unread') return effectiveUnread > 0;
          if (activeTab === 'unanswered') return session.last_message?.sender === 'user';
          if (activeTab === 'suspicious') return contactTags.includes(SUSPICIOUS_CONTACT_TAG);
          return true;
        };

        if (!shouldIncludeChat()) {
          const chatIndexToRemove = get().chats.findIndex((c) => c.id === adjustedSession.id);
          if (chatIndexToRemove !== -1) {
            const chats = [...get().chats];
            chats.splice(chatIndexToRemove, 1);
            set({ chats: get().sortChats(chats, 'newest') });
          }
          return;
        }

        const chatIndex = get().chats.findIndex((c) => c.id === adjustedSession.id);

        // Recalcular effectiveUnread con los datos ajustados
        const adjustedUMV = adjustedSession.users_messages_viewed?.find((u) => u.user_id === currentUserId);
        const finalEffectiveUnread = isCurrentOpen ? 0 : (adjustedUMV?.unread_count || 0);

        if (chatIndex === -1) {
          // Crear nuevo chat - toda la data ya viene del stream
          const newChat = {
            id: adjustedSession.id,
            unread_count: finalEffectiveUnread,
            last_message_date: adjustedSession.last_message_at,
            last_message_id: adjustedSession.last_message_id,
            text: adjustedSession.last_message?.text || '',
            last_message_text: adjustedSession.last_message?.text || '',
            last_message_metadata: adjustedSession.last_message?.metadata || {},
            last_msg_sender: adjustedSession.last_message?.sender || '',
            ...lastMessageReceiptFields(adjustedSession.last_message),
            source: adjustedSession.source,
            metadata: adjustedSession.metadata || {},
            messages_viewed: adjustedSession.messages_viewed || [],
            users_messages_viewed: adjustedSession.users_messages_viewed || [],
            manual_unread: adjustedUMV?.manual_unread || false,

            // Información del contacto ya viene denormalizada
            contact_name: adjustedSession.contact?.name || '',
            contact_last_name: adjustedSession.contact?.last_name || '',
            contact_email: adjustedSession.contact?.email || '',
            contact_phone: adjustedSession.contact?.phone || '',
            contact_tags: adjustedSession.contact?.tags || [],

            ai_enabled: adjustedSession.ai_enabled || false,
            disable_followup: adjustedSession.disable_followup ?? false,
            bot_id: adjustedSession.bot_id,
            lead_status_id: adjustedSession.contact?.bot_contacts?.find((bcl: any) => bcl.bot_id === adjustedSession.bot_id)?.lead_status_id || null,
            sales_assigned_id: adjustedSession.sales_assigned_id,
            sales_assigned_name: getAgentNameById(adjustedSession.sales_assigned_id),
            created_at: adjustedSession.created_at,
          };

          // Verificar filtro de lead status
          const { leadStatusFilter } = get();
          if (leadStatusFilter && leadStatusFilter !== '{}') {
            const contactLeadStatusId = adjustedSession.contact?.bot_contacts?.find((bcl: any) => bcl.bot_id === adjustedSession.bot_id)?.lead_status_id;
            if (!adjustedSession.contact || contactLeadStatusId !== leadStatusFilter) {
              return;
            }
          }

          // Check viewMyAssigned filter
          if (filters?.viewMyAssigned && adjustedSession.sales_assigned_id !== currentUserId) {
            return;
          }

          // Check viewAsAgent filter (when impersonating)
          if (filters?.viewAsAgent && filters?.viewAsSales && adjustedSession.sales_assigned_id !== filters.viewAsAgent) {
            return;
          }

          // Verificar filtros de etiquetas
          if (filters?.selectedTags && filters.selectedTags.length > 0) {
            const contactTags = adjustedSession.contact?.tags || [];
            const hasNoTags = filters.selectedTags.includes('No tags');
            const validTags = filters.selectedTags.filter((tag) => tag !== 'No tags');

            let shouldInclude = false;

            if (hasNoTags && validTags.length === 0) {
              // Solo "No tags" seleccionado
              shouldInclude = contactTags.length === 0;
            } else if (validTags.length > 0) {
              if (hasNoTags) {
                // Incluir tanto etiquetas válidas como "No tags"
                shouldInclude = contactTags.length === 0 || validTags.some((tag) => contactTags.includes(tag));
              } else {
                // Solo etiquetas válidas
                shouldInclude = validTags.some((tag) => contactTags.includes(tag));
              }
            }

            if (!shouldInclude) {
              return;
            }
          }

          const newChats = [newChat, ...get().chats];
          const sortedChats = get().sortChats(newChats, 'newest'); // En modo stream siempre newest
          set({ chats: sortedChats });
        } else {
          // Check viewMyAssigned filter
          if (filters?.viewMyAssigned && adjustedSession.sales_assigned_id !== currentUserId) {
            // Si el chat existente ya no cumple con los filtros, removerlo
            const chats = [...get().chats];
            chats.splice(chatIndex, 1);
            set({ chats: get().sortChats(chats, 'newest') });
            return;
          }

          // Check viewAsAgent filter (when impersonating)
          if (filters?.viewAsAgent && filters?.viewAsSales && adjustedSession.sales_assigned_id !== filters.viewAsAgent) {
            // Si el chat existente ya no cumple con los filtros, removerlo
            const chats = [...get().chats];
            chats.splice(chatIndex, 1);
            set({ chats: get().sortChats(chats, 'newest') });
            return;
          }

          // Verificar filtros de etiquetas para chat existente
          if (filters?.selectedTags && filters.selectedTags.length > 0) {
            const contactTags = adjustedSession.contact?.tags || [];
            const hasNoTags = filters.selectedTags.includes('No tags');
            const validTags = filters.selectedTags.filter((tag) => tag !== 'No tags');

            let shouldInclude = false;

            if (hasNoTags && validTags.length === 0) {
              // Solo "No tags" seleccionado
              shouldInclude = contactTags.length === 0;
            } else if (validTags.length > 0) {
              if (hasNoTags) {
                // Incluir tanto etiquetas válidas como "No tags"
                shouldInclude = contactTags.length === 0 || validTags.some((tag) => contactTags.includes(tag));
              } else {
                // Solo etiquetas válidas
                shouldInclude = validTags.some((tag) => contactTags.includes(tag));
              }
            }

            if (!shouldInclude) {
              // Si el chat existente ya no cumple con los filtros, removerlo
              const chats = [...get().chats];
              chats.splice(chatIndex, 1);
              set({ chats: get().sortChats(chats, 'newest') }); // En modo stream siempre newest
              return;
            }
          }

          // Actualizar chat existente
          const chats = [...get().chats];
          const existingChat = chats[chatIndex];

          chats[chatIndex] = {
            ...existingChat,
            unread_count: finalEffectiveUnread,
            last_message_date: adjustedSession.last_message_at,
            last_message_id: adjustedSession.last_message_id,
            text: adjustedSession.last_message?.text || '',
            last_message_text: adjustedSession.last_message?.text || '',
            last_message_metadata: adjustedSession.last_message?.metadata || {},
            last_msg_sender: adjustedSession.last_message?.sender || '',
            ...lastMessageReceiptFields(adjustedSession.last_message),
            ai_enabled: adjustedSession.ai_enabled || false,
            disable_followup: adjustedSession.disable_followup ?? existingChat.disable_followup ?? false,
            contact_tags: adjustedSession.contact?.tags || [],
            lead_status_id: adjustedSession.contact?.bot_contacts?.find((bcl: any) => bcl.bot_id === adjustedSession.bot_id)?.lead_status_id,
            manual_unread: isCurrentOpen ? false : (adjustedUMV?.manual_unread || false),
            users_messages_viewed: adjustedSession.users_messages_viewed || [],
            sales_assigned_id: adjustedSession.sales_assigned_id,
            sales_assigned_name: getAgentNameById(adjustedSession.sales_assigned_id),
          };

          const sortedChats = get().sortChats(chats, 'newest'); // En modo stream siempre newest

          if (isCurrentOpen) {
            set({
              chats: sortedChats,
              currentChat: chats[chatIndex],
            });
          } else {
            set({ chats: sortedChats });
          }
        }
      }) || []);
    } catch (error) {
      console.error('Error updating chats from bot sessions stream:', error);
    }
  },

  updateChats: async (updatedBlock: any, activeTab: string) => {
    try {
      await Promise.all(updatedBlock.data.user_messages_viewed_stream?.map(async (s) => {
        const currentUserId = useUserStore.getState().user.uid;
        // 1. Obtener el último mensaje
        const lastMessageRes = await graphqlClient.query({
          query: GetSessionLastMessage,
          variables: { session_id: s.session_id },
          fetchPolicy: 'no-cache',
        });

        // 2. Obtener los detalles de la sesión
        const sessionDetailsRes = await graphqlClient.query({
          query: GetBotSession,
          variables: {
            session_id: s.session_id,
            user_id: useUserStore.getState().user.uid,
            bot_id: useUserStore.getState().currentBot.id,
          },
          fetchPolicy: 'no-cache',
        });

        const sessionDetails = sessionDetailsRes.data.bot_sessions[0];
        const lastMessage = lastMessageRes.data.messages[0];

        const isCurrentOpen = get().currentChat?.id === s.session_id;
        const effectiveUnread = isCurrentOpen ? 0 : s.unread_count;

        const sessionContactTags = sessionDetails?.contact?.tags || [];
        const shouldIncludeChat = () => {
          if (activeTab === 'unread') return s.unread_count > 0;
          if (activeTab === 'unanswered') return lastMessage?.sender === 'user';
          if (activeTab === 'suspicious') return sessionContactTags.includes(SUSPICIOUS_CONTACT_TAG);
          return true;
        };

        if (!shouldIncludeChat()) {
          const chatIndexToRemove = get().chats.findIndex((c) => c.id === s.session_id);
          if (chatIndexToRemove !== -1) {
            const chats = [...get().chats];
            chats.splice(chatIndexToRemove, 1);
            set({ chats: get().sortChats(chats, 'newest') });
          }
          return;
        }

        const chatIndex = get().chats.findIndex((c) => c.id === s.session_id);

        if (chatIndex === -1) {
          const newChat = {
            id: s.session_id,
            unread_count: effectiveUnread,
            last_message_date: lastMessage.created_at,
            text: lastMessage.text,
            last_msg_sender: lastMessage.sender || '',
            last_message_metadata: lastMessage.metadata || {},
            ...lastMessageReceiptFields(lastMessage),
            source: sessionDetails?.source || 'whatsapp',
            metadata: sessionDetails?.metadata || {},
            messages_viewed: sessionDetails?.messages_viewed || [],
            users_messages_viewed: [
              {
                user_id: currentUserId,
                session_id: s.session_id,
                unread_count: effectiveUnread,
                last_msg_read_id: sessionDetails?.users_messages_viewed?.[0]?.last_msg_read_id,
                manual_unread: isCurrentOpen ? false : (sessionDetails?.users_messages_viewed?.[0]?.manual_unread ?? false),
                updated_at: new Date().toISOString(),
              },
            ],
            contact_name: sessionDetails?.contact?.name || '',
            contact_last_name: sessionDetails?.contact?.last_name || '',
            contact_email: sessionDetails?.contact?.email || '',
            contact_phone: sessionDetails?.contact?.phone || '',
            contact_tags: sessionDetails?.contact?.tags || [],
            ai_enabled: sessionDetails?.ai_enabled || false,
            disable_followup: sessionDetails?.disable_followup ?? false,
            bot_id: sessionDetails?.bot_id || useUserStore.getState().currentBot.id,
            lead_status_id: sessionDetails?.contact?.bot_contacts?.find((bcl: any) => bcl.bot_id === sessionDetails?.bot_id)?.lead_status_id || null,
            sales_assigned_id: sessionDetails?.sales_assigned_id || null,
            sales_assigned_name: getAgentNameById(sessionDetails?.sales_assigned_id || null),
          };

          // Verificar filtro de lead status
          const { leadStatusFilter } = get();
          if (leadStatusFilter && leadStatusFilter !== '{}') {
            const contactLeadStatusId = sessionDetails?.contact?.bot_contacts?.find((bcl: any) => bcl.bot_id === sessionDetails?.bot_id)?.lead_status_id;
            if (!sessionDetails?.contact || contactLeadStatusId !== leadStatusFilter) {
              return;
            }
          }

          const newChats = [newChat, ...get().chats];
          set({ chats: get().sortChats(newChats, 'newest') }); // En modo stream siempre newest
        } else {
          // Actualizar chat existente
          const chats = [...get().chats];
          const existingChat = chats[chatIndex];
          // Actualizar UMV para el usuario actual
          const backendUMVForCurrent = sessionDetails?.users_messages_viewed?.find((x) => x.user_id === currentUserId);
          const updatedUMV = Array.isArray(existingChat.users_messages_viewed)
            ? existingChat.users_messages_viewed.map((u) => (u.user_id === currentUserId
              ? {
                ...u,
                unread_count: typeof effectiveUnread === 'number' ? effectiveUnread : (backendUMVForCurrent?.unread_count ?? 0),
                last_msg_read_id: backendUMVForCurrent?.last_msg_read_id ?? u.last_msg_read_id,
                manual_unread: isCurrentOpen ? false : (typeof backendUMVForCurrent?.manual_unread === 'boolean' ? backendUMVForCurrent.manual_unread : (u.manual_unread ?? false)),
                updated_at: new Date().toISOString(),
              }
              : u))
            : [];
          if (!updatedUMV.find((u) => u.user_id === currentUserId)) {
            updatedUMV.push({
              user_id: currentUserId,
              session_id: s.session_id,
              unread_count: typeof effectiveUnread === 'number' ? effectiveUnread : (backendUMVForCurrent?.unread_count ?? 0),
              last_msg_read_id: backendUMVForCurrent?.last_msg_read_id,
              manual_unread: isCurrentOpen ? false : (typeof backendUMVForCurrent?.manual_unread === 'boolean' ? backendUMVForCurrent.manual_unread : (existingChat?.manual_unread ?? false)),
              updated_at: new Date().toISOString(),
            });
          }

          const myUnreadAfterExisting = (updatedUMV.find((u) => u.user_id === currentUserId)?.unread_count) ?? null;

          chats[chatIndex] = {
            ...existingChat,
            unread_count: effectiveUnread,
            last_message_date: lastMessage.created_at,
            text: lastMessage.text,
            last_msg_sender: lastMessage.sender || existingChat.last_msg_sender,
            last_message_metadata: lastMessage.metadata || existingChat.last_message_metadata || {},
            ...lastMessageReceiptFields(lastMessage),
            ai_enabled: sessionDetails?.ai_enabled || false,
            disable_followup: sessionDetails?.disable_followup ?? existingChat.disable_followup ?? false,
            contact_tags: sessionDetails?.contact?.tags || [],
            lead_status_id: sessionDetails?.contact?.bot_contacts?.find((bcl: any) => bcl.bot_id === sessionDetails?.bot_id)?.lead_status_id,
            manual_unread: isCurrentOpen ? false : existingChat?.manual_unread,
            users_messages_viewed: updatedUMV,
            sales_assigned_id: sessionDetails?.sales_assigned_id || existingChat?.sales_assigned_id,
            sales_assigned_name: getAgentNameById(sessionDetails?.sales_assigned_id || existingChat?.sales_assigned_id || null),
          };
          const myUnreadAfterUpsert = (updatedUMV.find((u) => u.user_id === currentUserId)?.unread_count) ?? null;
          set({ chats: get().sortChats(chats, 'newest') }); // En modo stream siempre newest
        }
      }) || []);
    } catch (error) {
      console.error('Error updating chats:', error);
    }
  },

  getLastMessageDate: (chat) => {
    if (chat.messages && chat.messages.length > 0) {
      return new Date(chat.messages[chat.messages.length - 1].created_at).getTime();
    }
    return new Date(0).getTime();
  },

  loadMoreChats: async (botId, limit, offset, leadStatus = null, whereClause = {}, sortOrder = 'newest') => {
    if (offset === 0) {
      return 0;
    }

    try {
      set({ loadingMore: true });
      const { hasMore } = get();

      // Definir el order_by según sortOrder y si hay filtro de fecha aplicado
      const hasDateFilter = whereClause && 'last_message_at' in whereClause && whereClause.last_message_at && (whereClause.last_message_at as any)._gte;
      const useAscOrder = sortOrder === 'oldest' || (sortOrder === 'fromDate' && hasDateFilter);
      const orderBy = useAscOrder
        ? [{ last_message_at: 'asc_nulls_last' }, { created_at: 'asc' }]
        : [{ last_message_at: 'desc_nulls_last' }, { created_at: 'desc' }];

      const variables: VariablesType = {
        bot_id_param: botId,
        limit,
        offset,
        where: {
          ...whereClause,
          ...(leadStatus && leadStatus !== '{}' ? {
            contact: leadStatus === 'null'
              ? { bot_contacts: { lead_status_id: { _is_null: true } } }
              : { bot_contacts: { lead_status_id: { _eq: leadStatus } } },
          } : {}),
        },
        order_by: orderBy,
      };

      const res = await graphqlClient.query({
        query: GetSessionMessagesNew,
        variables,
        fetchPolicy: 'no-cache',
      });

      if (res.data) {
        const transformedChats = transformSessionResponse(res.data.bot_sessions);

        set((state) => {
          const updatedChats = [...state.chats, ...transformedChats];
          return { chats: updatedChats };
        });

        set({ offset: offset + limit });

        // Stop when we get fewer chats than the limit (indicates last page)
        if (transformedChats.length < limit) {
          set({ hasMore: false });
        }
        return transformedChats.length;
      }
      return 0;
    } catch (error) {
      console.error('❌ Load More Error:', error);
      return 0;
    } finally {
      set({ loadingMore: false });
    }
  },

  fetchChats: async (botId, limit = 20, offset = 0, leadStatus = null, whereClause = {}, sortOrder = 'newest') => {
    try {
      set({ loading: true });

      // Definir el order_by según sortOrder y si hay filtro de fecha aplicado
      const hasDateFilter = whereClause && 'last_message_at' in whereClause && whereClause.last_message_at && (whereClause.last_message_at as any)._gte;
      const useAscOrder = sortOrder === 'oldest' || (sortOrder === 'fromDate' && hasDateFilter);
      const orderBy = useAscOrder
        ? [{ last_message_at: 'asc_nulls_last' }, { created_at: 'asc' }]
        : [{ last_message_at: 'desc_nulls_last' }, { created_at: 'desc' }];

      const variables: VariablesType = {
        bot_id_param: botId,
        limit,
        offset,
        where: {
          ...whereClause,
          ...(leadStatus && leadStatus !== '{}' ? {
            contact: leadStatus === 'null'
              ? { bot_contacts: { lead_status_id: { _is_null: true } } }
              : { bot_contacts: { lead_status_id: { _eq: leadStatus } } },
          } : {}),
        },
        order_by: orderBy,
      };

      const res = await graphqlClient.query({
        query: GetSessionMessagesNew,
        variables,
        fetchPolicy: 'no-cache',
      });
      if (res.data) {
        const transformedChats = transformSessionResponse(res.data.bot_sessions);
        const isDefaultFetch = offset === 0
          && sortOrder === 'newest'
          && !leadStatus
          && Object.keys(whereClause).length === 0;

        set({
          chats: transformedChats,
          offset: limit,
          hasMore: transformedChats.length >= limit,
          isPristineEmpty: isDefaultFetch && transformedChats.length === 0,
        });
      } else {
        toast.error('No se encontraron chats con esa etiqueta');
      }
    } catch (error) {
      console.error('Error fetching chats:', error);
    } finally {
      set({ loading: false });
    }
  },

  loadBotWhatappTemplates: async (botID: string) => {
    try {
      const res: ApiResponseType<TemplatesResponseType> = await api.get(`wa/${botID}/templates`);

      if (res.data.error === true) {
        toast.error(`Error al obtener los templates: ${res.data.message}`);
        return;
      }

      if (res.data.templates.data) {
        const approvedTemplates = res.data.templates.data.filter((template) => template.status === 'APPROVED');
        set({ whatsappTemplates: approvedTemplates });
      }
    } catch (error) {
      console.error('error', error);
      toast.error('loadBotWhatappTemplates: Ha ocurrido un error');
    }
  },

  changeSelectedTemplate: async (botID: string, template: WhatsappTemplateType) => {
    try {
      set({ loadingTemplate: true });
      const session_id = get().currentChat.id;
      const res: ApiResponseType<TemplateWithComponents> = await api.get(`wa/${botID}/templates/components?template_name=${template.name}&lang_code=${template.language}&session_id=${session_id}`);

      if (res.data) {
        set({
          loadedTemplate: {
            template,
            details: res.data,
          },
        });
      } else {
        throw new Error();
      }
    } catch (error) {
      toast.error('changeSelectedTemplate: Ha ocurrido un error');
    } finally {
      set({ loadingTemplate: false });
    }
  },

  deselectTemplate: () => {
    set({ loadedTemplate: null });
  },

  performKeywordSearch: async (botId: string, userId: string, searchTerm: string, offset = 0) => {
    try {
      if (offset === 0) {
        set({
          keywordSearchLoading: true,
          keywordSearchResults: null,
          keywordSearchOffset: 0,
          keywordSearchHasMore: true,
        });
      }

      // REST also matches the contact's email and name; sales only get their own chats.
      const res = await api.get(`bots/${botId}/chats`, { q: searchTerm, limit: SEARCH_PAGE_SIZE, offset });
      if (!res.success) throw new Error(`Chat search failed (${res.status ?? 'network'})`);

      // Keep the REST item and add the flat fields the list rows read.
      const enrichedResults = (res.data.items || []).map((item: any) => ({
        ...item,
        contact_id: item.contact?.id ?? null,
        contact_name: item.contact?.name ?? null,
        contact_last_name: item.contact?.last_name ?? null,
        contact_phone: item.contact?.phone ?? null,
        contact_email: item.contact?.email ?? null,
        last_message_text: item.last_message?.text ?? null,
        last_message_date: item.last_message?.created_at ?? item.last_message_at ?? null,
        last_message_metadata: item.last_message?.metadata || {},
        last_msg_sender: item.last_message?.sender || '',
        unread_count: item.viewed?.unread_count ?? 0,
        sales_assigned_name: getAgentNameById(item.sales_assigned_id),
      }));
      set((state) => ({
        keywordSearchResults: offset === 0 ? enrichedResults : [...(state.keywordSearchResults || []), ...enrichedResults],
        keywordSearchLoading: false,
        keywordSearchOffset: offset + SEARCH_PAGE_SIZE,
        keywordSearchHasMore: !!res.data.has_more,
      }));
    } catch (error) {
      console.error('Error performing keyword search:', error);
      // A failed "load more" keeps the rows already shown.
      set((state) => ({
        keywordSearchResults: offset === 0 ? [] : state.keywordSearchResults,
        keywordSearchLoading: false,
        keywordSearchHasMore: false,
      }));
    }
  },

  clearKeywordSearch: () => {
    set({
      keywordSearchResults: null,
      keywordSearchLoading: false,
      keywordSearchOffset: 0,
      keywordSearchHasMore: true,
    });
  },

  updateContact: (sessionId: string, contactInfo: ContactInfo) => {
    const currChat = get().currentChat;
    set((state) => {
      const contact = state.contactsMap[sessionId];
      return {
        contactsMap: {
          ...state.contactsMap,
          [sessionId]: contact ? { ...contact, ...contactInfo } : contactInfo,
        },
        currentChat: {
          ...currChat,
          contact_name: contactInfo.name || '',
          contact_last_name: contactInfo.last_name || '',
        },
        chats: state.chats.map((chat) => (chat.id === sessionId ? { ...chat, contact_name: contactInfo.name || '', contact_last_name: contactInfo.last_name || '' } : chat)),
      };
    });
  },

  updateChatsState: (updatedChats: any[]) => {
    set({ chats: updatedChats });
  },

  postNewMessage: async (bot_id: string, message: string, track_id: string) => {
    get().addOptimisticMessage(track_id, message, 'sending');
    return get().dispatchMessage(bot_id, message, track_id);
  },

  addOptimisticMessage: (
    track_id: string,
    message: string,
    localStatus: 'sending' | 'buffering' = 'sending',
  ) => {
    const { user } = useUserStore.getState();

    const optimisticMessage = {
      track_id,
      text: message,
      sender: 'human',
      created_at: new Date().toISOString(),
      localStatus,
      sent_at: null,
      delivered_at: null,
      read_at: null,
      failed_at: null,
      error_code: null,
      error_message: null,
      metadata: {
        type: 'text',
        track_id,
        sender_id: user.uid,
        sender_name: user.name || '',
        sender_email: user.email || '',
      },
    };

    set((state) => ({
      messages: [...state.messages, optimisticMessage],
    }));
  },

  updateOptimisticMessage: (
    track_id: string,
    updates: { text?: string; localStatus?: 'sending' | 'buffering' | 'error' | null },
  ) => {
    set((state) => ({
      messages: state.messages.map((msg) => (msg.track_id === track_id
        ? { ...msg, ...updates }
        : msg)),
    }));
  },

  dispatchMessage: async (bot_id: string, message: string, track_id: string) => (
    postWaTrackedMessage(set, get, {
      bot_id,
      track_id,
      content: message,
      action: 'dispatchMessage',
    })
  ),

  retryMessage: async (track_id: string, bot_id: string) => {
    const messageToRetry = get().messages.find((msg) => msg.track_id === track_id);

    if (!messageToRetry) {
      Sentry.captureMessage('retryMessage: Message not found', {
        level: 'warning',
        tags: { component: 'chatStore', action: 'retryMessage' },
        extra: { track_id, bot_id },
      });
      return;
    }

    set((state) => ({
      messages: state.messages.map((msg) => (msg.track_id === track_id
        ? {
          ...msg,
          localStatus: 'sending',
          failed_at: null,
          error_code: null,
          error_message: null,
        }
        : msg)),
    }));

    await postWaTrackedMessage(set, get, {
      bot_id,
      track_id,
      content: messageToRetry.text,
      action: 'retryMessage',
    });
  },

  sendMediaMessage: async (bot_id: string, media: File | Resource, mediaType: string, track_id: string) => {
    const fromResource = isResource(media);
    const showsLocalPreview = mediaType === 'image' || mediaType === 'video' || mediaType === 'sticker';
    let previewUrl: string | null = null;
    if (fromResource) {
      previewUrl = media.file_url;
    } else if (showsLocalPreview) {
      previewUrl = URL.createObjectURL(media);
    }

    const buildOptimisticMediaPayload = () => {
      if (mediaType === 'document') {
        if (fromResource) {
          const extension = media.mime_type?.split('/').pop()?.toLowerCase()
            || media.name.split('.').pop()?.toLowerCase()
            || 'file';
          return { file_name: media.name, extension, media_url: media.file_url };
        }
        const extension = media.name.split('.').pop()?.toLowerCase() || 'txt';
        return { file_name: media.name, extension };
      }
      if (previewUrl) {
        return { media_url: previewUrl };
      }
      return {};
    };

    try {
      const { user } = useUserStore.getState();
      const { currentChat } = get();

      if (!currentChat) {
        if (!fromResource && previewUrl) URL.revokeObjectURL(previewUrl);
        throw new Error('No current chat selected');
      }

      // Crear mensaje optimista
      const optimisticMessage = {
        track_id,
        text: '',
        sender: 'human',
        created_at: new Date().toISOString(),
        localStatus: 'sending',
        sent_at: null,
        delivered_at: null,
        read_at: null,
        failed_at: null,
        error_code: null,
        error_message: null,
        metadata: {
          type: mediaType,
          [mediaType]: buildOptimisticMediaPayload(),
          track_id,
          sender_id: user.uid,
          sender_name: user.name || '',
          sender_email: user.email || '',
        },
      };

      set((state) => ({
        messages: [...state.messages, optimisticMessage],
      }));

      const token = await auth.currentUser.getIdToken();
      const BASE_URL = process.env.NEXT_PUBLIC_API_URL;

      const recipient_phone_number = extractWhatsappNumber(currentChat?.metadata?.local_session_id);
      const formData = new FormData();
      formData.append('session_id', currentChat?.id);
      formData.append('to', recipient_phone_number);
      formData.append('message_type', mediaType);
      formData.append('track_id', track_id);
      if (fromResource) {
        formData.append('resource_id', media.id);
      } else {
        formData.append('file', media);
      }

      const response = await fetch(`${BASE_URL}/wa/${bot_id}/media_message`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      let result: WaMediaMessageResponse = {};
      try {
        result = await response.json();
      } catch {
        result = {};
      }

      const explicitFailure = result.status === 'failed'
        || (result.error != null && result.status !== 'sent');
      if (!response.ok || explicitFailure) {
        const failure = resolveMediaSendFailure(
          plainMediaErrorCode(result.error),
          mediaResponseErrorText(result),
        );
        set((state) => ({
          messages: state.messages.map((msg) => (msg.track_id === track_id
            ? {
              ...msg,
              localStatus: 'error',
              failed_at: new Date().toISOString(),
              error_message: failure.message,
              error_code: failure.code ?? null,
            }
            : msg)),
        }));
        if (failure.promptRecharge) {
          await promptInsufficientCredits();
        } else {
          toast.error(failure.message);
        }
        return;
      }

      const nestedMedia = result[mediaType];
      const nestedMediaUrl = nestedMedia
        && typeof nestedMedia === 'object'
        && typeof (nestedMedia as { media_url?: unknown }).media_url === 'string'
        ? (nestedMedia as { media_url: string }).media_url
        : null;
      const remoteMediaUrl = typeof result.media_url === 'string'
        ? result.media_url
        : nestedMediaUrl;

      const previous = get().messages.find((msg) => msg.track_id === track_id);
      const previousUrl = previous?.metadata?.[mediaType]?.media_url;
      if (
        typeof previousUrl === 'string'
        && previousUrl.startsWith('blob:')
        && remoteMediaUrl
        && remoteMediaUrl !== previousUrl
      ) {
        URL.revokeObjectURL(previousUrl);
      }

      set((state) => ({
        messages: state.messages.map((msg) => {
          if (msg.track_id !== track_id) return msg;

          const mediaPayload = mediaType === 'document'
            ? msg.metadata[mediaType]
            : remoteMediaUrl
              ? { media_url: remoteMediaUrl }
              : msg.metadata[mediaType];

          return {
            ...msg,
            localStatus: null,
            sent_at: new Date().toISOString(),
            failed_at: null,
            error_message: null,
            error_code: null,
            metadata: {
              ...msg.metadata,
              message_id: result.message_id ?? msg.metadata?.message_id,
              [mediaType]: mediaPayload,
            },
          };
        }),
      }));
    } catch (error) {
      console.error('Error sending media:', error);
      const errorMessage = error instanceof Error ? error.message : 'Error enviando mensaje';
      set((state) => ({
        messages: state.messages.map((msg) => (msg.track_id === track_id
          ? {
            ...msg,
            localStatus: 'error',
            failed_at: new Date().toISOString(),
            error_message: errorMessage,
            error_code: msg.error_code ?? null,
          }
          : msg)),
      }));
      toast.error('Error enviando mensaje');
    }
  },

  sendAudioMessage: async (bot_id: string, audio_url: string, track_id: string, session_id: string) => {
    try {
      const token = await auth.currentUser.getIdToken();
      const BASE_URL = process.env.NEXT_PUBLIC_API_URL;
      const currentChat = get().chats.find((chat) => chat.id === session_id);
      if (!currentChat) {
        throw new Error('No current chat selected');
      }
      const recipient_phone_number = extractWhatsappNumber(currentChat?.metadata?.local_session_id);
      const payload = {
        to: recipient_phone_number,
        audio_url,
        session_id,
        track_id,
      };

      const response = await fetch(`${BASE_URL}/wa/${bot_id}/audio_message`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error('Failed to send audio message');
      }
    } catch (error) {
      console.error('Error sending audio message:', error);
      toast.error('Error enviando mensaje de audio');
    }
  },

  postInstagramMessage: async (
    session_id: string,
    message: string,
    track_id: string,
    channel?: string,
  ) => {
    get().addOptimisticMessage(track_id, message, 'sending');
    return get().dispatchInstagramMessage(session_id, message, track_id, channel);
  },

  dispatchInstagramMessage: async (
    session_id: string,
    message: string,
    track_id: string,
    channel?: string,
  ) => {
    const { currentChat } = get();

    try {
      const payload: any = {
        session_id,
        message,
        track_id,
        ...(channel ? { channel } : {}),
      };

      const endpoint = getOutboundMessagingEndpoint(currentChat?.source);

      const response = await api.post(endpoint, payload);

      if (!response) {
        throw new Error('No response received from server');
      }

      if (response.success === false || !response.success) {
        const errorMessage = extractSendErrorMessage(response.data, 'Failed to send message');
        throw new Error(errorMessage);
      }

      const result = response.data;

      if (!result) {
        throw new Error('Invalid response data from server');
      }

      let isMessageSent = false;
      // send-message (messenger, telegram, gmail) answers success, not status.
      if (getOutboundMessagingEndpoint(currentChat?.source) === 'messaging/send-message') {
        isMessageSent = result.success === true && result.message_saved;
      } else {
        isMessageSent = result.status === 'sent';
      }

      if (isMessageSent) {
        set((state) => ({
          messages: state.messages.map((msg) => (msg.track_id === track_id
            ? {
              ...msg,
              localStatus: null,
              sent_at: new Date().toISOString(),
              failed_at: null,
              error_message: null,
              error_code: null,
              metadata: {
                ...msg.metadata,
                message_id: result.message_id,
                ...(result.channel && { channel: result.channel }),
              },
            }
            : msg)),
        }));
      } else {
        const errorMessage = extractSendErrorMessage(result, 'Failed to send message');
        throw new Error(errorMessage);
      }

      return result;
    } catch (error: any) {
      console.error('Error sending message:', error);
      const errorMessage = error?.message || 'Failed to send message';
      set((state) => ({
        messages: state.messages.map((msg) => (msg.track_id === track_id
          ? {
            ...msg,
            ...buildFailedMessageFields(errorMessage),
          }
          : msg)),
      }));
      const errorMsgBySource: Record<string, string> = {
        messenger: 'Error enviando mensaje de Messenger',
        telegram: 'Error enviando mensaje de Telegram',
      };
      const errorMsg = errorMsgBySource[currentChat?.source ?? ''] || 'Error enviando mensaje de Instagram';
      toast.error(errorMsg);
      throw error;
    }
  },

  sendInstagramMedia: async (
    session_id: string,
    media: File | Resource,
    mediaType: string,
    track_id: string,
    channel?: string,
  ) => {
    const { user } = useUserStore.getState();
    const { currentBot } = useUserStore.getState();
    const { currentChat } = get();
    const fromResource = isResource(media);
    const objectUrl = fromResource ? media.file_url : URL.createObjectURL(media);

    const optimisticMessage = {
      track_id,
      text: ' ',
      sender: 'human',
      created_at: new Date().toISOString(),
      localStatus: 'sending',
      sent_at: null,
      delivered_at: null,
      read_at: null,
      failed_at: null,
      error_code: null,
      error_message: null,
      metadata: {
        type: mediaType,
        [mediaType]: { media_url: objectUrl },
        track_id,
        sender_id: user.uid,
        sender_name: user.name || '',
        sender_email: user.email || '',
      },
    };

    set((state) => ({
      messages: [...state.messages, optimisticMessage],
    }));

    try {
      const uploadWithTimeout = (uploadPromise, timeout = 30000) => {
        let timeoutId;
        return Promise.race([
          uploadPromise,
          new Promise((_, reject) => {
            timeoutId = setTimeout(() => {
              reject(new Error('Upload timeout - please try again'));
            }, timeout);
          }),
        ]).finally(() => {
          if (timeoutId) {
            clearTimeout(timeoutId);
          }
        });
      };

      const media_url: string = fromResource
        ? media.file_url
        : await uploadWithTimeout(
          handleUploadFiles(media, currentBot.id),
          30000,
        );

      if (!media_url || typeof media_url !== 'string' || media_url.length === 0) {
        throw new Error('Error uploading media file - invalid response');
      }

      const payload: any = {
        session_id,
        message: ' ',
        track_id,
        media_type: mediaType,
        media_url,
        message_type: mediaType,
        ...(channel ? { channel } : {}),
      };

      // Differentiate endpoint based on source
      const endpoint = getOutboundMessagingEndpoint(currentChat?.source);

      const response = await api.post(endpoint, payload);

      if (!response) {
        throw new Error('No response received from server');
      }

      if (response.success === false || !response.success) {
        throw new Error(extractSendErrorMessage(response.data, 'Failed to send media message'));
      }

      const result = response.data;

      if (!result) {
        throw new Error('Invalid response data from server');
      }

      // Validate based on channel
      let isMessageSent = false;
      // send-message (messenger, telegram, gmail) answers success, not status.
      if (getOutboundMessagingEndpoint(currentChat?.source) === 'messaging/send-message') {
        isMessageSent = result.success === true && result.message_saved;
      } else {
        isMessageSent = result.status === 'sent';
      }

      if (isMessageSent) {
        set((state) => ({
          messages: state.messages.map((msg) => (msg.track_id === track_id
            ? {
              ...msg,
              localStatus: null,
              sent_at: new Date().toISOString(),
              failed_at: null,
              error_message: null,
              error_code: null,
              metadata: {
                ...msg.metadata,
                message_id: result.message_id,
                ...(result.channel && { channel: result.channel }),
                [mediaType]: { media_url },
              },
            }
            : msg)),
        }));
      } else {
        throw new Error(extractSendErrorMessage(result, 'Failed to send media message'));
      }

      return result;
    } catch (error) {
      console.error('Error sending media:', error);
      const errorMessage = (error as Error)?.message || 'Failed to send media message';

      set((state) => ({
        messages: state.messages.map((msg) => (msg.track_id === track_id
          ? {
            ...msg,
            ...buildFailedMessageFields(errorMessage),
          }
          : msg)),
      }));
      const errorMsgBySource: Record<string, string> = {
        messenger: 'Error enviando multimedia de Messenger',
        telegram: 'Error enviando multimedia de Telegram',
      };
      const errorMsg = errorMsgBySource[currentChat?.source ?? '']
        || 'Error enviando multimedia de Instagram';
      toast.error(errorMsg);
      throw error;
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  },

  sendGmailReply: async (session_id: string, reply: EmailReply, track_id: string) => {
    const { user } = useUserStore.getState();
    const file = reply.attachment;
    // The card shows the reply at once; on failure it is removed and the modal keeps the text.
    set((state) => ({
      messages: [...state.messages, {
        track_id,
        text: reply.text,
        sender: 'human',
        created_at: new Date().toISOString(),
        localStatus: 'sending',
        sent_at: null,
        failed_at: null,
        metadata: {
          type: file ? file.type : 'text',
          ...(file ? {
            [file.type]: { media_url: file.url, file_name: file.name, mime_type: file.mime_type },
          } : {}),
          html: reply.html,
          track_id,
          sender_id: user?.uid,
          sender_name: user?.name || '',
          sender_email: user?.email || '',
        },
      }],
    }));
    const drop = () => set((state) => ({ messages: state.messages.filter((m) => m.track_id !== track_id) }));

    const response = await api.post(
      getOutboundMessagingEndpoint('gmail'),
      emailReplyPayload(session_id, reply, track_id),
    );
    const result = response?.data;
    if (!emailReplySent(response)) {
      drop();
      return { ok: false, message: extractSendErrorMessage(result, '') };
    }
    set((state) => ({
      messages: state.messages.map((msg) => (msg.track_id === track_id
        ? {
          ...msg,
          localStatus: null,
          sent_at: new Date().toISOString(),
          metadata: { ...msg.metadata, message_id: result.message_id },
        }
        : msg)),
    }));
    return { ok: true };
  },

  addOptimisticNote: (noteData: any, contactData?: any) => {
    if (!noteData) return;

    // Transformar la nota del formato de mutación al formato del backend
    const optimisticNoteMessage = {
      id: `note-${noteData.id}`,
      created_at: noteData.created_at,
      text: noteData.content, // content -> text
      sender: 'note',
      metadata: {
        type: 'note',
        note_type: noteData.type,
        is_pinned: noteData.is_pinned,
        created_by: noteData.created_by,
        note_data: {
          id: noteData.id,
          content: noteData.content,
          type: noteData.type,
          is_pinned: noteData.is_pinned,
          created_by: noteData.created_by,
          created_at: noteData.created_at,
          updated_at: noteData.updated_at,
          __typename: 'bot_session_notes',
        },
      },
      contact: contactData || {
        id: noteData.contact_id,
        name: '',
        last_name: '',
        email: '',
        phone: '',
        __typename: 'contacts',
      },
    };

    // Agregar la nota optimista a los mensajes
    set((state) => ({
      messages: [...state.messages, optimisticNoteMessage],
    }));
  },

  addOptimisticEvent: (eventData: any, eventId: string, createdAt: string, contactData?: any) => {
    if (!eventData || !eventId || !createdAt) return;

    // Transformar el evento al formato del backend
    const optimisticEventMessage = {
      id: eventId, // Ya viene con prefijo "event-"
      created_at: createdAt,
      text: eventData.description,
      sender: 'event',
      metadata: {
        type: 'event',
        event_type: eventData.event_type,
        event_category: eventData.event_category,
        severity: eventData.severity,
        created_by_name: eventData.created_by_name,
        created_by_type: 'user',
        event_data: {
          id: eventId.replace('event-', ''), // Sin prefijo para el event_data
          event_type: eventData.event_type,
          event_category: eventData.event_category,
          description: eventData.description,
          metadata: eventData.metadata || {},
          created_by_type: 'user',
          created_by_name: eventData.created_by_name,
          created_at: createdAt,
          severity: eventData.severity,
          __typename: 'bot_session_events',
        },
      },
      contact: contactData || {
        id: '',
        name: '',
        last_name: '',
        email: '',
        phone: '',
        __typename: 'contacts',
      },
    };

    // Agregar el evento optimista a los mensajes
    set((state) => ({
      messages: [...state.messages, optimisticEventMessage],
    }));
  },

  setSidebarEventCallbacks: (callbacks: { addEvent?: (event: any) => void }) => {
    set({ sidebarEventCallbacks: callbacks });
  },

  addOptimisticEventToSidebar: (eventData: any, eventId: string, createdAt: string) => {
    if (!eventData || !eventId || !createdAt) return;

    // Crear evento en formato del sidebar (más simple que el del chat)
    const sidebarEvent = {
      id: eventId.replace('event-', ''), // Sin prefijo para el sidebar
      session_id: get().currentChat?.id || '',
      event_type: eventData.event_type,
      event_category: eventData.event_category,
      description: eventData.description,
      metadata: eventData.metadata || {},
      created_by: get().currentChat?.contact?.id || '',
      created_by_type: 'user',
      created_by_name: eventData.created_by_name,
      created_at: createdAt,
      severity: eventData.severity,
      __typename: 'bot_session_events',
    };

    // Llamar al callback del sidebar si existe
    const { addEvent } = get().sidebarEventCallbacks;
    if (addEvent) {
      addEvent(sidebarEvent);
    }
  },
}));

const transformSessionResponse = (sessions: any[]) => sessions.map((session) => {
  const lastMessage = session.last_message || {};
  const currentUserId = useUserStore.getState().user?.uid;
  const userMessageViewed = session.users_messages_viewed?.find((u) => u.user_id === currentUserId) || {};

  return {
    id: session.id,
    source: session.source,
    bot_id: session.bot_id,
    metadata: session.metadata,
    ai_enabled: session.ai_enabled,
    disable_followup: session.disable_followup ?? false,
    messages_viewed: session.messages_viewed,
    deleted_at: session.deleted_at,
    created_at: session.created_at,
    agent_id: session.agent_id,

    // Denormalizados/nuevos campos
    last_message_id: session.last_message_id,
    last_message_date: session.last_message_at,
    users_messages_viewed: session.users_messages_viewed,
    sales_assigned_id: session.sales_assigned_id, // ¡Esta línea faltaba!

    // Preview del último mensaje
    text: lastMessage?.text,
    last_message_metadata: lastMessage?.metadata,
    last_msg_sender: lastMessage?.sender,
    ...lastMessageReceiptFields(lastMessage),

    // Unread por usuario actual
    unread_count: userMessageViewed?.unread_count || 0,
    last_msg_read_id: userMessageViewed?.last_msg_read_id,
    manual_unread: userMessageViewed?.manual_unread,

    // Contacto
    contact_id: session.contact?.id,
    contact_phone: session.contact?.phone,
    contact_name: session.contact?.name,
    contact_last_name: session.contact?.last_name,
    contact_email: session.contact?.email || session.contact_email || null,
    contact_tags: session.contact?.tags || [],
    lead_status_id: session.contact?.bot_contacts?.find((bcl: any) => bcl.bot_id === session.bot_id)?.lead_status_id,

    // Agente asignado
    sales_assigned_name: getAgentNameById(session.sales_assigned_id),
  };
});

export default useChatStore;
